import React, { useState } from "react";
import { colors } from "../shared/constants";
import { inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";
import { bm } from "./bmClient";

// The manager's Add/Edit tenant, slotted into the shared board in place of
// portal/TenantModal.
//
// Same props, same fields, same words — a different endpoint underneath. The
// office writes the asset register in two calls, POST /site-units/ then
// /unit-contacts/; a manager is firewalled off /customers/ entirely, and
// /api/bm/tenancies/ takes the unit and its primary contact flattened into one
// request. Either way it is the same register row, so an edit here shows up on
// the office's asset register and vice versa.
export function BMTenantModal({ siteId, areaId, row, onClose, onSaved }) {
  const isEdit = !!row;
  const [form, setForm] = useState({
    unit_number: row?.unit_number || "",
    name: row?.unit_name || "",
    floor_level: row?.floor_level || "",
    contact_name: row?.tenant_name || "",
    contact_mobile: row?.tenant_mobile || "",
    contact_email: row?.tenant_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.unit_number.trim() && !form.name.trim())
      return setError("Give the tenancy a unit number or a name.");

    setSaving(true);
    setError("");
    // Exactly the backend's write allowlist, and nothing else.
    const payload = {
      unit_number: form.unit_number,
      name: form.name,
      floor_level: form.floor_level,
      contact_name: form.contact_name,
      contact_mobile: form.contact_mobile,
      contact_email: form.contact_email,
    };
    try {
      if (isEdit) {
        await bm.patch(`/tenancies/${row.unit_id}/`, payload);
      } else {
        // site and area are accepted on create only — the server locks both
        // afterwards, so a tenancy cannot be walked to another building.
        await bm.post("/tenancies/", { ...payload, site: siteId, area: areaId });
      }
      onSaved();
    } catch (e) {
      const d = e.response?.data || {};
      setError(
        d.detail ||
          (Array.isArray(d.non_field_errors) && d.non_field_errors[0]) ||
          `Could not ${isEdit ? "save" : "add"} that tenant.`
      );
      setSaving(false);
    }
  };

  return (
    <SimpleModal
      title={isEdit ? "Edit tenant" : "Add tenant"}
      onClose={onClose}
      onSave={handleSave}
      saving={saving}
      error={error}
      saveLabel={isEdit ? "Save" : "Add"}
      wide
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "12px" }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Unit number</label>
          <input
            style={inputStyle}
            value={form.unit_number}
            onChange={(e) => set("unit_number", e.target.value)}
            placeholder="12"
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Tenancy name</label>
          <input
            style={inputStyle}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Bobs Burgers"
          />
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Floor / level</label>
        <input
          style={inputStyle}
          value={form.floor_level}
          onChange={(e) => set("floor_level", e.target.value)}
          placeholder="Ground Floor"
        />
      </div>

      <div
        style={{
          borderTop: `1px solid ${colors.border}`,
          margin: "4px 0 16px",
          paddingTop: "16px",
          fontSize: "12px",
          fontWeight: "700",
          color: colors.slate,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Primary contact
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Name</label>
        <input
          style={inputStyle}
          value={form.contact_name}
          onChange={(e) => set("contact_name", e.target.value)}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Mobile</label>
          <input
            style={inputStyle}
            value={form.contact_mobile}
            onChange={(e) => set("contact_mobile", e.target.value)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            value={form.contact_email}
            onChange={(e) => set("contact_email", e.target.value)}
          />
        </div>
      </div>
    </SimpleModal>
  );
}
